import { i18n } from '@/common/locale';
import { GroupSelectOption } from '@/common/components/molecules/select/wideDropdownSelect.setup';
import { SelectOption } from '@/types/common.types';
import { CUSTOM_STAT_PREFIX } from '@/common/utils/statInfo.define';

import type {
  ExcludePeriod,
  NotificationSchedule,
  WeeklySchedule,
} from '@/api-client/metav8Over/model';
import { IconType } from '@/types/icon.types';
import { TargetType } from '@/common/types/targetType.types';
import { AlertNotification, AlertRuleActionField } from './alert.type';
import { CheckInterval } from '../views/userAlert/alertRuleWindow.types';

const WEEKLY_SCHEDULE_DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export const DEFAULT_WEEKLY_SCHEDULE: WeeklySchedule = Object.fromEntries(
  WEEKLY_SCHEDULE_DAY_KEYS.map((key) => [key, 16777215]),
) as WeeklySchedule;

export const DEFAULT_EXCLUDE_PERIOD: ExcludePeriod = {
  fromDate: 0,
  fromTime: '00:00',
  toDate: 0,
  toTime: '23:59',
};

export const DEFAULT_NOTIFICATION_SCHEDULE: NotificationSchedule = {
  enabled: false,
  excludePeriodList: [],
  weeklySchedule: { ...DEFAULT_WEEKLY_SCHEDULE },
};

export const MAX_CONDITION_COUNT = 10;
export const MAX_FORMULA_COUNT = 10;

export const METRIC_HIDDEN_EVALUATE_VALUE = ['changePerSec', 'current'];

export const CLOUD_DB_RESOURCE_TARGET_TYPES: { name: string; value: TargetType }[] = [
  {
    name: 'AWS Aurora',
    value: 'aws_aurora',
  },
  {
    name: 'AWS DMS Replication Instance',
    value: 'aws_dms_replinstance',
  },
  {
    name: 'AWS DMS Task',
    value: 'aws_dms_task',
  },
  {
    name: 'AWS DocumentDB',
    value: 'aws_documentdb',
  },
  {
    name: 'AWS DynamoDB',
    value: 'aws_dynamodb',
  },
  {
    name: 'AWS EC2',
    value: 'aws_ec2',
  },
  {
    name: 'AWS ElastiCache Cluster',
    value: 'aws_elasticache_cluster',
  },
  {
    name: 'AWS ElastiCache Shard',
    value: 'aws_elasticache_shard',
  },
  {
    name: 'AWS ElastiCache Node',
    value: 'aws_elasticache_node',
  },
  {
    name: 'AWS ElastiCache Serverless',
    value: 'aws_elasticache_serverless',
  },
  {
    name: 'AWS Lambda',
    value: 'aws_lambda',
  },
  {
    name: 'AWS RDS',
    value: 'aws_rds',
  },
  {
    name: 'AWS Redshift',
    value: 'aws_redshift',
  },
  {
    name: 'AWS S3',
    value: 'aws_s3',
  },
  // {
  //   name: 'AWS VPC',
  //   value: 'aws_vpc',
  // },
  {
    name: 'Azure Blob Storage',
    value: 'azure_blob_storage',
  },
  {
    name: 'Azure Cache for Redis',
    value: 'azure_cache_for_redis',
  },
  {
    name: 'Azure Cosmos DB',
    value: 'azure_cosmos_db',
  },
  {
    name: 'Azure Database for MySQL',
    value: 'azure_database_for_mysql',
  },
  {
    name: 'Azure Database for PostgreSQL',
    value: 'azure_database_for_postgresql',
  },
  {
    name: 'Azure Functions',
    value: 'azure_functions',
  },
  {
    name: 'Azure Virtual Machines',
    value: 'azure_virtual_machines',
  },
];

export const ALERT_TARGET_TYPES: { name: string; value: TargetType }[] = [
  {
    name: 'Node',
    value: 'node',
  },
  {
    name: 'Pod',
    value: 'pod',
  },
  {
    name: 'Container',
    value: 'container',
  },
  {
    name: 'Job',
    value: 'job',
  },
  {
    name: 'Cluster',
    value: 'cluster',
  },
  {
    name: 'Application',
    value: 'application',
  },
  {
    name: 'Web Server',
    value: 'webserver',
  },
  {
    name: 'Database',
    value: 'database',
  },
  {
    name: 'Host',
    value: 'host',
  },
  {
    name: 'Network Device',
    value: 'network_device',
  },
  ...CLOUD_DB_RESOURCE_TARGET_TYPES,
  {
    name: 'Kafka Broker',
    value: 'kafka_broker',
  },
  {
    name: 'Kafka Cluster',
    value: 'kafka_cluster',
  },
  {
    name: 'Kafka Cluster Consumer Group',
    value: 'kafka_cluster_consumer_group',
  },
  {
    name: 'Prometheus',
    value: 'prometheus',
  },
];

export const ALERT_MAXGAUGE_TARGET_TYPES: { name: string; value: TargetType }[] = [
  {
    name: 'Database',
    value: 'database',
  },
  {
    name: 'Host',
    value: 'host',
  },
  {
    name: 'Container',
    value: 'container',
  },
  ...CLOUD_DB_RESOURCE_TARGET_TYPES,
];

export const ALWAYS_SHOW_TARGET_TYPES = [CUSTOM_STAT_PREFIX, 'target'];
type TargetTypeValue = (typeof ALERT_TARGET_TYPES)[number]['value'];
export const DATABASE_ADDITIONAL_SHOW_TARGET_TYPES: Partial<Record<TargetTypeValue, string[]>> = {
  database: ['filesystem'],
  host: ['filesystem'],
};

export const ALERT_NOTIFICATION_TYPE: Record<AlertNotification, string> = {
  email: 'email',
  slack: 'slack',
  telegram: 'telegram',
  sms: 'sms',
  webhook: 'webhook',
  onSite: 'onSite',
  kakaoTalk: 'kakaoTalk',
} as const;

export const ALERT_NOTIFICATION_TYPE_TEXT: Record<AlertNotification, string> = {
  email: 'EMAIL',
  slack: 'SLACK',
  telegram: 'TELEGRAM',
  sms: 'SMS',
  webhook: 'WEBHOOK',
  onSite: 'ON_SITE',
  kakaoTalk: 'KAKAOTALK',
} as const;

export const ALERT_NOTIFICATION_TYPE_ICON: Record<AlertNotification, IconType> = {
  email: 'icon-color-message',
  slack: 'icon-color-slack',
  telegram: 'icon-color-telegram',
  sms: 'icon-color-sms',
  webhook: 'icon-color-webhook',
  onSite: 'icon-color-on-site',
  kakaoTalk: 'icon-kakaotalk',
} as const;

export const ALERT_RULE_ACTION_FIELD: Record<string, AlertRuleActionField> = {
  activation: 'activation',
  mute: 'mute',
  copy: 'copy',
  isolatedSend: 'isolatedSend',
};

const MAXIMUM_OCCURRENCES_ITEMS = Array.from({ length: 10 }, (_, i) => ({
  name: `${i + 1}`,
  value: `${i + 1}`,
}));

export const getMaximumOccurrencesItems = (): GroupSelectOption => {
  return {
    '': [{ name: '∞', value: '-1' }, ...MAXIMUM_OCCURRENCES_ITEMS],
  };
};

export const getCheckIntervalItems = (): SelectOption<CheckInterval['unit']>[] => [
  { name: i18n.global.t('DESC.UI.SECOND'), value: 's' },
  { name: i18n.global.t('DESC.UI.MINUTE'), value: 'm' },
  { name: i18n.global.t('DESC.UI.HOUR'), value: 'h' },
  { name: i18n.global.t('DESC.UI.DAY'), value: 'd' },
];
